import React from "react"

import { Box, Grid, Typography } from "@material-ui/core"
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from "recharts"

import { themeColors } from "letalkui"
import { TrackableLinkInteractionDailySeriesEntry } from "@/protocols/trackableLinks"

import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions/InteractionsDailyChart/styles"

export type InteractionsDailyChartProps = {
	dailySeries: TrackableLinkInteractionDailySeriesEntry[]
}

type ChartDataEntry = {
	date: string
	totalClicked: number
	totalResponded: number
}

type LegendPayloadEntry = {
	dataKey?: string | number | ((obj: unknown) => unknown)
	color?: string
	value?: string
}

type LegendRenderProps = {
	payload?: ReadonlyArray<LegendPayloadEntry>
}

type TooltipPayloadEntry = {
	dataKey?: string
	value?: number
	color?: string
	name?: string
}

const TOTAL_CLICKED_COLOR = themeColors.colors.system.purple[500]
const TOTAL_RESPONDED_COLOR = themeColors.colors.system.green[600]
const CHART_LINE_STROKE_WIDTH = 3.419
const CHART_ACTIVE_DOT_RADIUS = 7

const formatChartTickDate = (value: string): string => {
	const [year, month, day] = value.split("-").map(Number)
	return new Date(year, month - 1, day).toLocaleDateString("pt-BR", { month: "short", day: "numeric" })
}

const InteractionsDailyChart: React.FC<InteractionsDailyChartProps> = ({ dailySeries }) => {
	const classes = useStyles()

	const chartLabels: Record<"totalClicked" | "totalResponded", string> = {
		totalClicked: "Total de cliques",
		totalResponded: "Cliques com resposta"
	}

	const chartData: ChartDataEntry[] = dailySeries.map((entry) => ({
		date: entry.date,
		totalClicked: entry.totalClicked,
		totalResponded: entry.totalResponded
	}))

	const hasChartData = chartData.length > 0

	const renderTooltipContent = (props: Record<string, unknown>) => {
		const active = props.active as boolean | undefined
		const payload = props.payload as TooltipPayloadEntry[] | undefined
		const label = props.label as string | undefined

		if (!active || !payload?.length || !label) {
			return null
		}

		return (
			<Box className={classes.tooltipContainer}>
				<Typography className={classes.tooltipDate}>
					{formatChartTickDate(label)}
				</Typography>
				{payload.map((entry) => (
					<Box key={entry.dataKey} className={classes.tooltipRow}>
						<Box className={classes.tooltipLabelWrapper}>
							<Box className={classes.tooltipColorDot} style={{ backgroundColor: entry.color }} />
							<Typography className={classes.tooltipLabel}>
								{entry.name}
							</Typography>
						</Box>
						<Typography className={classes.tooltipValue}>
							{entry.value}
						</Typography>
					</Box>
				))}
			</Box>
		)
	}

	const renderLegend = ({ payload }: LegendRenderProps) => {
		if (!payload) {
			return null
		}

		return (
			<Grid container spacing={3} justifyContent="center" className={classes.legendWrapper}>
				{payload.map((entry) => (
					<Grid item key={entry.dataKey as string} className={classes.legendItem}>
						<Box className={classes.legendColorDot} style={{ backgroundColor: entry.color }} />
						<Typography className={classes.legendText}>
							{entry.value}
						</Typography>
					</Grid>
				))}
			</Grid>
		)
	}

	if (!hasChartData) {
		return (
			<Box className={classes.emptyState}>
				<Typography variant="body2">
					Nenhum dado encontrado para o período selecionado
				</Typography>
			</Box>
		)
	}

	return (
		<ResponsiveContainer width="99%" height={300}>
			<LineChart data={chartData} margin={{ top: 12, right: 12, left: 4, bottom: 0 }}>
				<CartesianGrid vertical={false} stroke={themeColors.colors.system.gray[50]} strokeWidth={1} />
				<XAxis
					dataKey="date"
					axisLine={false}
					tickLine={false}
					tickMargin={8}
					minTickGap={32}
					tickFormatter={formatChartTickDate}
					tick={{ fontSize: 10, fill: themeColors.colors.system.gray[800] }}
				/>
				<YAxis
					allowDecimals={false}
					axisLine={false}
					tickLine={false}
					tickMargin={4}
					width={40}
					tick={{ fontSize: 10, fill: themeColors.colors.system.gray[800] }}
				/>
				<Tooltip content={renderTooltipContent} cursor={{ stroke: themeColors.colors.system.gray[200], strokeWidth: 1 }} />
				<Legend content={renderLegend} />
				<Line
					type="monotone"
					dataKey="totalClicked"
					name={chartLabels.totalClicked}
					stroke={TOTAL_CLICKED_COLOR}
					strokeWidth={CHART_LINE_STROKE_WIDTH}
					dot={false}
					activeDot={{ r: CHART_ACTIVE_DOT_RADIUS, fill: TOTAL_CLICKED_COLOR, strokeWidth: 0 }}
				/>
				<Line
					type="monotone"
					dataKey="totalResponded"
					name={chartLabels.totalResponded}
					stroke={TOTAL_RESPONDED_COLOR}
					strokeWidth={CHART_LINE_STROKE_WIDTH}
					dot={false}
					activeDot={{ r: CHART_ACTIVE_DOT_RADIUS, fill: TOTAL_RESPONDED_COLOR, strokeWidth: 0 }}
				/>
			</LineChart>
		</ResponsiveContainer>
	)
}

export default InteractionsDailyChart
